import { useClerk, useUser } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { fetchUserHomeData } from "~/api/userHome";
import GroupUsageDialog from "~/components/groups/GroupUsageDialog";
import { useAppState } from "~/hooks/useAppState";
import { useAppStore } from "~/store";

// Create a custom event to notify components when auth state changes
export const AUTH_STATE_CHANGE_EVENT = "auth-state-change";

// Helper function to dispatch auth state change event
export const notifyAuthStateChange = () => {
  const event = new CustomEvent(AUTH_STATE_CHANGE_EVENT, {
    detail: { timestamp: Date.now() }
  });
  window.dispatchEvent(event);
};

/**
 * Component that listens to Clerk authentication state changes
 * and fetches user data into the global state.
 *
 * This is used to ensure the user data is available throughout the app
 * immediately after login, regardless of which route the user is on.
 */
export const AuthStateListener = () => {
  const clerk = useClerk();
  const { user, isSignedIn, isLoaded } = useUser();
  const { userInfo, setUserInfo } = useAppState("userInfo", "setUserInfo");
  const setUserHomeData = useAppStore((state) => state.setUserHomeData);
  const setCurrentGroup = useAppStore((state) => state.setCurrentGroup);
  const [showGroupDialog, setShowGroupDialog] = useState(false);

  // Helper function to fetch user home data
  const fetchUserData = async () => {
    console.log("Fetching user home data from API...");
    try {
      const data = await fetchUserHomeData();

      if (data) {
        console.log("User home data fetched:", data);

        // This will set the userInfo and userHomeData in one call
        setUserHomeData(data);

        // Explicitly set currentGroup to the default group
        if (data.defaultGroup) {
          setCurrentGroup(data.defaultGroup);
        }

        // Check if default group has any members
        const hasMembers = data.groupMembers && data.groupMembers.length > 0;

        if (data.defaultGroup && !hasMembers) {
          console.log("Default group has no members, showing member dialog");
          // Dispatch custom event to open the InitialMemberDialogue
          const event = new CustomEvent("open-initial-member-dialogue");
          window.dispatchEvent(event);
        } else if (!data.defaultGroup) {
          // No default group, show group creation dialog
          setShowGroupDialog(true);
        }

        // Notify the app that auth state has changed
        notifyAuthStateChange();
      } else {
        console.error("Failed to fetch user home data");
      }
    } catch (error) {
      console.error("Error fetching user home data:", error);
    }
  };

  // Handle dialog close and refresh data
  const handleCloseDialog = async () => {
    setShowGroupDialog(false);

    // After creating a group, refresh data to update UI state everywhere
    try {
      // Add a small delay to ensure the server has time to process the new group
      setTimeout(async () => {
        await fetchUserData();
        // Notify app that state has changed to trigger re-renders where needed
        notifyAuthStateChange();
      }, 500);
    } catch (error) {
      console.error("Error refreshing data after group creation:", error);
    }
  };

  // Monitor authentication state changes
  useEffect(() => {
    // Don't do anything if Clerk hasn't loaded yet
    if (!isLoaded) return;

    if (isSignedIn) {
      // User is signed in, fetch their info if we don't have it
      if (!userInfo) {
        console.log("User is signed in but userInfo is not available, fetching now");
        fetchUserData();
      } else {
        // Even if we already have userInfo, notify components about auth state
        notifyAuthStateChange();
      }
    } else {
      // User is signed out, clear their info
      if (userInfo) {
        console.log("User signed out, clearing user info from global state");
        setUserInfo(undefined);
        setUserHomeData(undefined);
        setShowGroupDialog(false);
        notifyAuthStateChange();
      }
    }
  }, [isLoaded, isSignedIn, userInfo, setUserInfo]);

  // Handle page loads - this helps with refresh after login
  useEffect(() => {
    // Only run if we have a session but no user info yet
    if (clerk.session && isSignedIn && !userInfo) {
      console.log("Session detected on page load but no userInfo, fetching user data");
      fetchUserData();
    }
  }, [clerk.loaded, clerk.session, isSignedIn, userInfo]);

  // Return the actual component - only with the group creation dialog
  return (
    <>
      {showGroupDialog && userInfo && (
        <GroupUsageDialog
          isOpen={showGroupDialog}
          onClose={handleCloseDialog}
          userId={userInfo.id}
          suppressToast={true}
        />
      )}
    </>
  );
};

export default AuthStateListener;
